const {
  fetchBrowsingContext,
  getCookie,
  setCookie,
  getElements,
  viewportSize,
  shippingCountries,
  normalize,
} = window.BAO.Utils

class MarketSelector extends window.BAO.CustomElement() {
  static geoResolved = false
  static geoPending = false

  constructor () {
    super()
    this.onOpen = this.onOpen.bind(this)
    this.syncNativeCookie = this.syncNativeCookie.bind(this)
    this.syncCrossStoreCookie = this.syncCrossStoreCookie.bind(this)
    this.handleSearchInput = this.handleSearchInput.bind(this)
    this.handleLanguageChange = this.handleLanguageChange.bind(this)
  }

  connectedCallback () {
    super.connectedCallback()

    this.persistRegionParam()
    this.reopenAfterSwitch()

    if (this.beginAutoSelect()) return
    this.updateTrigger()
  }

  beginAutoSelect () {
    if (this.constructor.geoPending) return true
    if (this.constructor.geoResolved) return false
    if (window.Shopify?.designMode) return false
    if (getCookie('fs-geo')) return false

    const url = new URL(window.location)
    if (url.searchParams.get('region')) return false
    if (window.location.hash === '#geo') return false

    const countries = this.els.country?.elements || []
    if (!countries.length) return false

    this.constructor.geoResolved = true
    this.constructor.geoPending = true
    this.geoTimeout = setTimeout(() => this.finishAutoSelect(), 3000)

    this.resolveShippingCountry(url, countries)
    return true
  }

  async resolveShippingCountry (url, countries) {
    let handle = ''
    try {
      const {
        detected_values: { country: { handle: detected = '' } = {} } = {},
      } = await fetchBrowsingContext()
      handle = detected
    } catch (error) {
      return this.finishAutoSelect()
    }

    const target =
      handle && handle !== this.dataset.currentCountry
        ? countries.find(el => el.dataset.countryCode === handle)
        : null

    if (!target) return this.finishAutoSelect()

    if (target.tagName === 'A') {
      const targetUrl = new URL(target.href)

      if (targetUrl.origin === url.origin) {
        this.handleRedirect(handle, target)
        this.setGeoCokie({
          value: handle,
          name: target.dataset.countryname,
        })
      }
      return
    }

    const form = target.closest('form')
    if (!form) return this.finishAutoSelect()

    const returnTo = form.querySelector('[name="return_to"]')
    if (returnTo) returnTo.value = url.pathname + url.search

    this.setGeoCookie({
      value: handle,
      name: target.dataset.countryname,
    })
    clearTimeout(this.geoTimeout)

    form.submit()
  }

  finishAutoSelect () {
    if (!this.constructor.geoPending) return

    this.constructor.geoPending = false
    clearTimeout(this.geoTimeout)
    this.updateTrigger()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(document, 'drawerOpen', this.onOpen)
    this.listeners.add(document, 'panelOpened', this.onOpen)
    this.listeners.add(this, 'submit', this.syncNativeCookie)
    this.listeners.add(this, 'click', this.syncCrossStoreCookie)

    if (this.els.search?.exists) {
      this.listeners.add(
        this.els.search.element,
        'input',
        this.handleSearchInput,
      )
    }

    if (this.els.languageSelect?.exists) {
      this.listeners.add(
        this.els.languageSelect.element,
        'change',
        this.handleLanguageChange,
      )
    }
  }

  handleSearchInput (event) {
    const { value = '' } = event.target
    this.filterCountries(value)
  }

  disconnectedCallback () {
    this.listeners.remove(document, 'drawerOpen', this.onOpen)
    this.listeners.remove(document, 'panelOpened', this.onOpen)
    this.listeners.remove(this, 'submit', this.syncNativeCookie)
    this.listeners.remove(this, 'click', this.syncCrossStoreCookie)

    if (this.els.search?.exists) {
      this.listeners.remove(
        this.els.search.element,
        'input',
        this.handleSearchInput,
      )
    }

    if (this.els.languageSelect?.exists) {
      this.listeners.remove(
        this.els.languageSelect.element,
        'change',
        this.handleLanguageChange,
      )
    }
  }

  updateTrigger () {
    const geo = this.geoCookie
    const countryEls = this.externalEls.triggerCountry?.elements || []

    if (geo?.value) {
      countryEls.forEach(el => {
        el.textContent = geo.value
      })
    }

    const triggers = this.externalEls.trigger?.elements || []
    triggers.forEach(el => {
      el.style.visibility = 'visible'
    })
  }

  filterCountries (query) {
    const searchSelector = 'drw-MarketSelector-isSearching'
    const search = normalize(query.trim())
    const isSearching = search.length > 0
    const wasSearching = this.classList.contains(searchSelector)

    const els = this.els
    const continents = els.continent?.elements || []
    const countrySelector = els.country?.selector
    const itemSelector = els.countryItem?.selector
    const nameSelector = els.countryName?.selector

    if (isSearching && !wasSearching) {
      this.openBeforeSearch = new Map()
      continents.forEach(continent => {
        this.openBeforeSearch.set(continent, continent.open)
      })
    }

    this.classList.toggle(searchSelector, isSearching)

    continents.forEach(continent => {
      let hasMatch = false

      getElements(countrySelector, { context: continent }).forEach(country => {
        const name = country.dataset.countryName
        const index = search ? normalize(name).indexOf(search) : -1
        const match = !search || index !== -1

        const item = country.closest(itemSelector)
        if (item) item.hidden = !match
        if (match) hasMatch = true

        this.renderCountryName(
          country.querySelector(nameSelector),
          name,
          index,
          search.length,
        )
      })

      continent.hidden = !hasMatch

      if (isSearching) {
        continent.open = hasMatch
      } else if (this.openBeforeSearch?.has(continent)) {
        continent.open = this.openBeforeSearch.get(continent)
      }
    })

    if (!isSearching) {
      this.openBeforeSearch = null
    }
  }

  renderCountryName (nameEl, name, index, length) {
    if (!nameEl) return

    if (index === -1) {
      nameEl.textContent = name
      return
    }

    const match = document.createElement('mark')
    match.className = 'drw-MarketSelector_CountryMatch'
    match.textContent = name.slice(index, index + length)

    nameEl.replaceChildren(
      name.slice(0, index),
      match,
      name.slice(index + length),
    )
  }

  persistRegionParam () {
    const url = new URL(window.location)
    const region = url.searchParams.get('region')

    if (region) {
      const countries = this.els.country?.elements || []
      const country = countries.find(el => el.dataset.countryCode === region)

      this.setGeoCookie({
        value: region,
        name: country?.dataset.countryName,
      })

      url.searchParams.delete('region')
      history.replaceState(null, '', url.pathname + url.search + url.hash)
    }
  }

  onOpen (event) {
    if (event.detail?.key !== 'market-selector') return

    const search = this.els.search?.element
    if (search) search.value = ''
    this.filterCountries('')

    this.highlightSelectedCountry()
    this.updateShippingSummary()
  }

  updateShippingSummary () {
    const geo = this.geoCookie
    if (!geo) return

    const countryEl = this.els.shippingCountry?.element
    const currencyEl = this.els.shippingCurrency?.element

    const countries = this.els.country?.elements || []
    const name =
      geo.name ||
      countries.find(el => el.dataset.countryCode === geo.value)?.dataset
        .countryName

    if (countryEl && name) countryEl.textContent = name
    if (currencyEl && geo.currency) {
      currencyEl.textContent = geo.currency === 'EUR' ? '€' : '$'
    }
  }

  reopenAfterSwitch () {
    if (window.location.hash !== '#market-selector') return
    if (this.closest('.mob-NavigationPanel')) return

    history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search,
    )

    if (!this.els.languageSelect?.exists) return

    window.requestAnimationFrame(() => {
      const isDesktop = ['lg', 'xl'].includes(viewportSize())

      if (isDesktop) {
        document
          .querySelector('[data-drawers-trigger="market-selector"]')
          ?.click()
        return
      }

      document
        .querySelector('[data-drawers-trigger="mobile-navigation"]')
        ?.click()
      document
        .querySelector(
          '[data-mobile-navigation-el="button"][data-target="market-selector"]',
        )
        ?.click()
    })
  }

  highlightSelectedCountry () {
    const region = new URLSearchParams(window.location.search).get('region')
    const current = this.geoCookie?.value || region
    if (!current) return

    const els = this.els
    const countries = els.country?.elements || []
    const continents = els.continent?.elements || []
    const continentSelector = els.continent?.selector

    let selected = null

    countries.forEach(el => {
      if (el.dataset.countryCode === current) {
        selected = el
        el.setAttribute('aria-current', 'true')
      } else {
        el.removeAttribute('aria-current')
      }
    })

    if (!selected) return

    const openContinent = selected.closest(continentSelector)

    continents.forEach(details => {
      details.open = details === openContinent
    })
  }

  syncNativeCookie (event) {
    const form = event.target
    const selector = this.els.country?.selector
    const country = selector ? form.querySelector(selector) : null
    if (!country) return

    this.setGeoCookie({
      value: country.dataset.countryCode,
      name: country.dataset.countryName,
    })
  }

  syncCrossStoreCookie (event) {
    const selector = this.els.country?.selector
    const country = selector ? event.target.closest(selector) : null
    if (!country || country.tagName !== 'A') return

    this.setGeoCookie({
      value: country.dataset.countryCode,
      name: country.dataset.countryName,
    })
  }

  handleLanguageChange (event) {
    const { value } = event.target
    const geo = this.geoCookie
    if (!geo) return

    const location = shippingCountries().find(
      country => country.code === geo.value,
    )
    const supported = location?.languages ? Object.keys(location.languages) : []
    const language = this.matchLanguage(value, supported) || value

    setCookie('fs-geo', JSON.stringify({ ...geo, language }), 14)
  }

  setGeoCookie ({ value, name, currency }) {
    const location = shippingCountries().find(country => country.code === value)
    if (!location) return

    const language = this.detectLanguage(location)
    setCookie(
      'fs-geo',
      JSON.stringify({
        value: location.code,
        name: location.name,
        site: location.site,
        language,
      }),
      14,
    )
  }

  matchLanguage (languageCode, supported = []) {
    const code = languageCode.split('-')[0].toLowerCase()
    return supported.find(lang => lang.toLowerCase() === code)
  }

  detectLanguage (location) {
    const locationData = location
    if (!locationData?.languages) return

    const supported = Object.keys(locationData.languages)
    const browserLangs = navigator.languages || [navigator.language]

    for (const browserLang of browserLangs) {
      const match = this.matchLanguage(browserLang, supported)
      if (match) return match
    }

    return supported[0]
  }

  handleRedirect (handle, target) {
    const location = shippingCountries().find(
      country => country.code === handle,
    )

    if (!location) {
      window.location.href = target.href
      return
    }

    const language = this.detectLanguage(location)

    if (!language) {
      window.location.href = target.href
      return
    }

    const targetUrl = new URL(target.href)
    const marketPath = `/${location.languages[language]}`

    const cleanPath = targetUrl.pathname.replace(/^\/[a-z]{2}-[a-z]{2}/i, '')

    targetUrl.pathname = `${marketPath}${cleanPath}`

    window.location.href = targetUrl.href
  }

  get geoCookie () {
    try {
      return JSON.parse(getCookie('fs-geo'))
    } catch (error) {
      return null
    }
  }
}

if (!customElements.get('market-selector')) {
  customElements.define('market-selector', MarketSelector)
}
